import { useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TechnologyType } from '@/features/projects/types';
import { useCurrentUser } from '@/features/users/hooks';
import { parseMulti } from '@/utils';

import {
  getCapacityFromOpportunity,
  getCountriesFromOpportunity,
  getDevelopmentStatusFromOpportunity,
  getTechnologyTypesFromOpportunity,
} from '../helpers';

import { usePublishedOpportunityList } from './use-opportunity-list';

export function useFilteredOpportunities() {
  const [searchParams] = useSearchParams();
  const [reloadFlag, setReloadFlag] = useState(0);

  const { data, isLoading, isFetching } = usePublishedOpportunityList();
  const user = useCurrentUser();

  const datasetMaxCapacity = useMemo(() => {
    if (!data?.length) return 0;
    let max = 0;
    for (const opp of data) {
      const cap = Number(getCapacityFromOpportunity(opp)) || 0;
      if (cap > max) max = cap;
    }
    return max;
  }, [data]);

  const reload = useCallback(() => {
    setReloadFlag((prev) => prev + 1);
  }, []);

  const capacityToken = (searchParams.get('capacity') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)[0];

  let capacityRange: [number, number] | null = null;
  if (capacityToken) {
    const [a, b] = capacityToken.split('-').map((s) => s.trim());
    const min = Number(a);
    const max = Number(b);
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      capacityRange = [min, max];
    }
  }

  const opportunities = useMemo(
    () =>
      data?.filter((opportunity) => {
        const technologies = parseMulti(searchParams.get('technology'), {
          lower: true,
          unique: true,
        });

        const developmentStatuses = parseMulti(searchParams.get('development_status'), {
          unique: true,
        });

        const countries = parseMulti(searchParams.get('country'), {
          upper: true,
          unique: true,
        });

        const opportunityTechnologies = getTechnologyTypesFromOpportunity(opportunity);
        const opportunityDevelopmentStatus = getDevelopmentStatusFromOpportunity(opportunity);
        const opportunityCapacity = getCapacityFromOpportunity(opportunity);
        const opportunityCountries = getCountriesFromOpportunity(opportunity).map((c) => c.toUpperCase());

        if (
          technologies.length > 0 &&
          !technologies.some((t) => opportunityTechnologies.includes(t as TechnologyType))
        ) {
          return false;
        }

        if (
          developmentStatuses.length > 0 &&
          !developmentStatuses.some((ds) => opportunityDevelopmentStatus.includes(ds))
        ) {
          return false;
        }

        if (capacityRange) {
          const [min, max] = capacityRange;
          if (!(opportunityCapacity >= min && opportunityCapacity <= max)) {
            return false;
          }
        }

        if (countries.length > 0 && !countries.some((c) => opportunityCountries.includes(c))) {
          return false;
        }

        return true;
      }),
    [searchParams, data, user, reloadFlag]
  );

  return {
    opportunities,
    reload,
    datasetMaxCapacity,
    isLoading,
    isFetching,
  };
}
